import { createElement as rc, useState, useCallback, useEffect, useContext } from 'react';
import { Button, testProperties, contexts } from 'lib_ui-primitives';
import { default as _DropDown } from '../../_abstractComponent/DropDown';
import HNode from '../../../HNode';
import { ListRow, Container, LabelAndField } from './styles';
import PropTypes from 'prop-types';
import lodash from 'lodash';
const { merge } = lodash;
// value of dropdown must be set to something with an _id or the selectedItem
// styling comparison (which looks at _id) will always pass (i.e. undefined === undefined)
const DEFAULT_SELECTED_ITEM = { _id: 'default' };
const _p = {
    _DropDown
};
export default function DynamicFieldEntryRow(props) {
    const {
        entry,
        remove,
        index,
        currentRoute,
        allowedEntries,
        entryUsed,
        parentHNode,
        propertyPath = parentHNode?.propertyPath || ''
    } = props ?? {};
    // prefer parent scanning settings
    const {
        displayScanButton = false,
        scanRfid = false,
        scanBarcode = false,
        displayRfidPowerButton = false
    } = parentHNode ?? {};

    // Before the user selects a field (i.e. hNode), the entry just has an id and some
    // scanning settings. After the user selects a field, the entry will be updated to
    // contain the selected hNode.
    const [childHNode, setChildHNode] = useState({
        ...entry,
        propertyPath,
        displayScanButton,
        scanRfid,
        scanBarcode,
        displayRfidPowerButton
    });
    const focusContext = useContext(contexts.FocusContext);

    // Intercept the remove button click so we can pass the information necessary to
    // remove the entry.
    const subFormRemove = useCallback(() => {
        remove(index, childHNode);
    }, [index, remove, childHNode]);

    // If the type of data (i.e. hNode) for this row has not been set yet, this intercepts
    // the normal setValue function, so that the selected hNode can be set here.
    // If the hNode is already set, this won't be called and the normal setValue will be used
    // so the control behaves like any other form element (e.g. like a ShortText, DataPicker, etc.).
    const setChildHNodeProxy = useCallback(
        hNode => {
            focusContext.disableFocusManagement = true;
            merge(entry, hNode, { displayScanButton, scanRfid, scanBarcode, displayRfidPowerButton, propertyPath });
            setChildHNode(entry);
            entryUsed(index, entry);
        },
        [
            index,
            entryUsed,
            entry,
            displayScanButton,
            scanRfid,
            scanBarcode,
            focusContext,
            displayRfidPowerButton,
            propertyPath
        ]
    );

    // If the user selects the field type (i.e. hNode), this puts the focus back into the
    // new field so the actual data can be populated.
    // e.g. if a user selects that the field should be for updating a serial number, this
    // puts the focus back into the serial number field so the user can enter a value.
    useEffect(() => {
        return focusContext.onChange((type, entry) => {
            if (entry.id === childHNode.id && type === 'add') {
                entry.focus?.();
            }
        });
    }, [childHNode.id, focusContext]);

    // prettier-ignore
    return rc(ListRow, null,
        rc(Container, null,
            rc(LabelAndField, null,
                // Render a dropdown allowing the user to select the type of field (i.e.
                // hNode) they want to add.
                !childHNode.hNodeType && rc(_p._DropDown, {
                    records: allowedEntries,
                    hNode: {
                        displayUnassignedRow: false,
                        id: `fdd${index}`,
                        propertyName: 'title',
                        placeholder: 'Field',
                        // Ensure new entries have a position at the beginning
                        treePosition: { sequence: parentHNode.treePosition.minSequence }
                    },
                    value: DEFAULT_SELECTED_ITEM,
                    setValue: setChildHNodeProxy,
                    active: false // usually a required value but not relevant here (there is no label to move when active)
                }),
                // Normal field rendering for the selected hNode
                !!childHNode.hNodeType && rc(HNode, {
                    hNode: childHNode,
                    key: index,
                    currentRoute
                })
            )
        ),
        rc(Button, {
            icon: 'clear',
            buttonStyle: 'round',
            color: 'error',
            onClick: subFormRemove,
            ...testProperties(parentHNode, 'clear', index)
        })
    );
}

DynamicFieldEntryRow.propTypes = {
    hNode: PropTypes.shape({
        treePosition: PropTypes.shape({
            sequence: PropTypes.number.isRequired
        }).isRequired
    }).isRequired
};
